import { useAuthStore } from '@/stores/authStore'
import { API_URL } from '@/lib/utils/constants'
import type { ApiError, AuthTokens, RefreshResponse } from '@/types'

interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, string | number | boolean | undefined>
  body?: unknown
  responseType?: 'json' | 'blob'
}

class APIClient {
  private baseURL: string

  constructor(baseURL: string) {
    this.baseURL = baseURL
  }

  private refreshes = new Map<string, Promise<void>>()

  private async refreshSession(tokens: AuthTokens): Promise<void> {
    const existing = this.refreshes.get(tokens.refreshToken)
    if (existing) return existing
    const refresh = (async () => {
      const response = await fetch(this.buildURL('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      })
      if (!response.ok) {
        if ([400, 401, 403].includes(response.status) && useAuthStore.getState().tokens === tokens) {
          useAuthStore.getState().clearAuth()
        }
        throw { status: response.status, code: 'REFRESH_FAILED', message: 'Your session could not be refreshed. Please try again.' }
      }
      const data: RefreshResponse = await response.json()
      if (!data.access_token) throw new Error('Invalid refresh response')
      // A response from an old session must never restore a signed-out user.
      if (useAuthStore.getState().tokens === tokens) {
        useAuthStore.getState().setTokens({ ...tokens, accessToken: data.access_token, expiresIn: data.expires_in })
      }
    })()
    this.refreshes.set(tokens.refreshToken, refresh)
    try { await refresh } finally { this.refreshes.delete(tokens.refreshToken) }
  }

  private buildURL(endpoint: string, params?: RequestOptions['params']): string {
    // Remove leading slash from endpoint to properly join with baseURL
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
    // Ensure baseURL ends with / for proper joining
    const cleanBaseURL = this.baseURL.endsWith('/') ? this.baseURL : `${this.baseURL}/`
    const url = new URL(cleanEndpoint, cleanBaseURL)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url.toString()
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}, retried = false): Promise<T> {
    const { params, body, responseType, ...fetchOptions } = options
    const url = this.buildURL(endpoint, params)
    const tokens = useAuthStore.getState().tokens
    const publicAuth = /^\/?auth\/(login|register|refresh)$/.test(endpoint)
    const headers = new Headers(options.headers)
    headers.set('Content-Type', 'application/json')
    if (tokens && !publicAuth) headers.set('Authorization', `Bearer ${tokens.accessToken}`)

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })

    if (response.status === 401 && tokens && !publicAuth && !retried) {
      const current = useAuthStore.getState().tokens
      if (current === tokens) await this.refreshSession(tokens)
      const refreshed = useAuthStore.getState().tokens
      if (refreshed && refreshed.refreshToken === tokens.refreshToken && refreshed.accessToken !== tokens.accessToken) {
        return this.request<T>(endpoint, options, true)
      }
    }

    if (!response.ok) {
      if (response.status === 401 && !publicAuth && useAuthStore.getState().tokens === tokens) {
        useAuthStore.getState().clearAuth()
      }
      const data = await response.json().catch(() => ({}))
      const payload = typeof data.error === 'object' && data.error !== null ? data.error : {}
      const error: ApiError = {
        status: response.status,
        code: payload.code ?? 'REQUEST_FAILED',
        message: payload.message ?? (typeof data.error === 'string' ? data.error : data.detail) ?? response.statusText,
        details: payload.details,
        request_id: payload.request_id,
        timestamp: payload.timestamp,
      }
      throw error
    }

    if (responseType === 'blob') return await response.blob() as T

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return null as T
    }

    const text = await response.text()
    return text ? JSON.parse(text) : (null as unknown as T)
  }

  async get<T>(endpoint: string, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async getBlob(endpoint: string): Promise<Blob> {
    return this.request<Blob>(endpoint, { method: 'GET', responseType: 'blob' })
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body })
  }

  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body })
  }

  async patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body })
  }

  async delete<T = void>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

export const apiClient = new APIClient(API_URL)
